import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase, { supabaseConfigured } from '../supabase/supabase';
import QuoteStepCoverage from '../components/quote/QuoteStepCoverage';
import QuoteStepEstimate, { AGENT_ESTIMATE_TYPES } from '../components/quote/QuoteStepEstimate';
import QuoteStepApplicant from '../components/quote/QuoteStepApplicant';
import QuoteStepCoverageDetails from '../components/quote/QuoteStepCoverageDetails';
import QuoteStepLossHistory from '../components/quote/QuoteStepLossHistory';
import QuoteStepPriorInsurance from '../components/quote/QuoteStepPriorInsurance';
import QuoteStepDocuments from '../components/quote/QuoteStepDocuments';
import QuoteStepReview from '../components/quote/QuoteStepReview';
import { AGENCY_PHONE_DISPLAY, AGENCY_EMAIL, AGENCY_EMAIL_MAILTO } from '../constants/contact';

const TOTAL_STEPS = 8;

// AGENT_ESTIMATE_TYPES imported from QuoteStepEstimate — coverage types with no self-serve estimate

const STEP_NAMES = [
  'Coverage Type',
  'Quick Estimate',
  'Your Information',
  'Coverage Details',
  'Loss History',
  'Prior Insurance',
  'Documents',
  'Review & Submit',
];

const INITIAL_FORM = {
  // Step 1
  coverage_type: '',
  // Step 2 — personal
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  date_of_birth: '',
  address_street: '',
  address_city: '',
  address_state: 'CA',
  address_zip: '',
  // Step 2 — commercial
  business_legal_name: '',
  business_dba: '',
  business_fein: '',
  business_entity_type: '',
  business_year_est: '',
  business_phone: '',
  business_website: '',
  business_description: '',
  // Step 3 — coverage-specific (keyed by type)
  form_data_auto: {},
  form_data_home: {},
  form_data_renters: {},
  form_data_bop: {},
  form_data_cgl: {},
  form_data_wc: {},
  form_data_commercial_auto: {},
  form_data_umbrella: {},
  // Step 4
  has_prior_losses: false,
  loss_history: [],
  prior_cancellation: false,
  prior_cancellation_explanation: '',
  // Step 5
  prior_carrier: '',
  prior_policy_expiration: '',
  years_continuously_insured: '',
  // Quick Estimate (step 2 for auto/home)
  estimate_zip: '',
  estimate_driver_age: '',
  estimate_vehicle_count: '',
  estimate_year_built: '',
  estimate_dwelling_value: '',
  estimate_low: null,
  estimate_high: null,
  estimate_factors: null,
};

const COMMERCIAL_TYPES = [
  'Business Owners Policy',
  'Commercial General Liability',
  'Workers Compensation',
  'Commercial Auto',
  'Umbrella',
];

export default function QuotePage() {
  const [step, setStep] = useState(0); // 0 = landing
  const [form, setForm] = useState(INITIAL_FORM);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [fileWarnings, setFileWarnings] = useState([]);
  const [confirmedQuoteId, setConfirmedQuoteId] = useState('');
  const navigate = useNavigate();

  const isCommercial = COMMERCIAL_TYPES.includes(form.coverage_type);
  const skipEstimate = AGENT_ESTIMATE_TYPES.includes(form.coverage_type);

  const updateForm = useCallback((updates) => {
    setForm(prev => ({ ...prev, ...updates }));
  }, []);

  // When advancing from step 1, skip to step 3 if coverage type has no estimate
  const goNext = () => {
    setStep(s => {
      const next = s + 1;
      if (next === 2 && skipEstimate) return 3;
      return Math.min(next, TOTAL_STEPS);
    });
  };

  // When going back from step 3, skip over step 2 if coverage type has no estimate
  const goBack = () => {
    setStep(s => {
      const prev = s - 1;
      if (prev === 2 && skipEstimate) return 1;
      return Math.max(prev, 1);
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');
    setFileWarnings([]);

    if (!supabaseConfigured) {
      setSubmitError(
        'This form is not yet configured to save data. If you are the site owner, ' +
        'please add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to your Vercel ' +
        `environment variables and redeploy. To reach us directly, call ${AGENCY_PHONE_DISPLAY}.`
      );
      setSubmitting(false);
      return;
    }

    try {
      const payload = {
        coverage_type: form.coverage_type,
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone,
        date_of_birth: form.date_of_birth || null,
        address_street: form.address_street,
        address_city: form.address_city,
        address_state: form.address_state,
        address_zip: form.address_zip,
        business_legal_name: form.business_legal_name || null,
        business_dba: form.business_dba || null,
        business_fein: form.business_fein || null,
        business_entity_type: form.business_entity_type || null,
        business_year_est: form.business_year_est ? parseInt(form.business_year_est) : null,
        business_phone: form.business_phone || null,
        business_website: form.business_website || null,
        business_description: form.business_description || null,
        form_data_auto: form.form_data_auto,
        form_data_home: form.form_data_home,
        form_data_renters: form.form_data_renters,
        form_data_bop: form.form_data_bop,
        form_data_cgl: form.form_data_cgl,
        form_data_wc: form.form_data_wc,
        form_data_commercial_auto: form.form_data_commercial_auto,
        form_data_umbrella: form.form_data_umbrella,
        has_prior_losses: form.has_prior_losses,
        loss_history: form.loss_history,
        prior_cancellation: form.prior_cancellation,
        prior_cancellation_explanation: form.prior_cancellation_explanation || null,
        prior_carrier: form.prior_carrier || null,
        prior_policy_expiration: form.prior_policy_expiration || null,
        years_continuously_insured: form.years_continuously_insured ? parseInt(form.years_continuously_insured) : null,
        source: 'Web',
        estimate_low:     form.estimate_low     || null,
        estimate_high:    form.estimate_high    || null,
        estimate_factors: form.estimate_factors || null,
      };

      const quoteId = crypto.randomUUID();
      const { error: qrErr } = await supabase
        .from('quote_requests')
        .insert({ ...payload, id: quoteId });

      if (qrErr) throw qrErr;
      const warnings = [];

      for (const file of uploadedFiles) {
        const filePath = `${quoteId}/${file.name}`;
        const { error: uploadErr } = await supabase.storage
          .from('quote-documents')
          .upload(filePath, file.file, { contentType: file.type });

        if (uploadErr) {
          warnings.push(file.name);
          continue;
        }

        await supabase.from('quote_request_documents').insert({
          quote_request_id: quoteId,
          file_name: file.name,
          file_path: filePath,
          file_size: file.size,
          mime_type: file.type,
          document_type: 'Other',
        });
      }

      setFileWarnings(warnings);
      setConfirmedQuoteId(quoteId.slice(0, 8));
      setStep(9); // confirmation screen
    } catch (err) {
      const isNetworkError =
        err instanceof TypeError && err.message === 'Failed to fetch';
      const detail = err?.message || err?.details || JSON.stringify(err);
      setSubmitError(
        isNetworkError
          ? 'Could not reach the server. Please check your internet connection and try again, ' +
            `or call us at ${AGENCY_PHONE_DISPLAY}.`
          : `Something went wrong saving your request. Please try again or call us at ${AGENCY_PHONE_DISPLAY}. (${detail})`
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Landing screen
  if (step === 0) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4">
        <div className="max-w-lg w-full text-center space-y-8">
          <div>
            <div className="text-2xl font-bold text-[#1e3a5f] mb-1">
              Pinoy General Insurance Services
            </div>
            <div className="w-16 h-1 bg-[#c9a84c] mx-auto rounded-full"></div>
          </div>
          <div>
            <h1 className="text-4xl font-bold text-[#1e3a5f] leading-tight mb-4">
              Get a Free Insurance Quote
            </h1>
            <p className="text-lg text-gray-600">
              No spam, no pressure — just straightforward coverage options tailored for you.
            </p>
          </div>
          <div className="space-y-3 text-left bg-gray-50 rounded-xl p-6">
            {['Takes about 5 minutes to complete', 'No account or login required', 'We will reach out within 1 business day'].map((item, i) => (
              <div key={i} className="flex items-center gap-3 text-gray-700">
                <div className="w-5 h-5 rounded-full bg-[#1e3a5f] flex items-center justify-center shrink-0">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span>{item}</span>
              </div>
            ))}
          </div>
          <button
            onClick={() => setStep(1)}
            className="w-full bg-[#1e3a5f] hover:bg-[#162d4a] text-white font-semibold text-lg py-4 rounded-xl transition-colors"
          >
            Get My Free Quote
          </button>
          <p className="text-sm text-gray-500">
            17304 Norwalk Blvd, Cerritos, CA 90703
          </p>
        </div>
      </div>
    );
  }

  // Confirmation screen
  if (step === 9) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4">
        <div className="max-w-lg w-full text-center space-y-6">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-10 h-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#1e3a5f] mb-2">Quote Request Received!</h1>
            <p className="text-gray-600">
              We will reach out within 1 business day to discuss your options.
            </p>
          </div>
          <div className="bg-[#1e3a5f] text-white rounded-xl p-6">
            <div className="text-sm text-blue-200 mb-1">Your Reference Number</div>
            <div className="text-3xl font-mono font-bold tracking-wider">{confirmedQuoteId.toUpperCase()}</div>
          </div>
          {form.estimate_low && form.estimate_high && (
            <div className="text-sm text-gray-600">
              <p className="font-medium text-gray-800">
                Your estimate range: ${form.estimate_low}–${form.estimate_high}/yr ({form.coverage_type}, ZIP {form.estimate_zip})
              </p>
              <p className="mt-1 text-xs text-gray-500">Final premium is subject to underwriting.</p>
            </div>
          )}
          {fileWarnings.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left text-sm text-yellow-800">
              <strong>Note:</strong> Your form was submitted but the following file(s) could not be uploaded:{' '}
              {fileWarnings.join(', ')}. You can email them to{' '}
              <a href={AGENCY_EMAIL_MAILTO} className="underline">{AGENCY_EMAIL}</a>.
            </div>
          )}
          <button
            onClick={() => navigate('/')}
            className="text-[#1e3a5f] font-semibold underline text-sm"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100 shadow-sm">
        <div className="h-1 bg-gray-100">
          <div
            className="h-full bg-[#1e3a5f] transition-all duration-500"
            style={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
          />
        </div>
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="text-[#1e3a5f] font-semibold text-sm">
            Pinoy General Insurance Services
          </div>
          <div className="text-sm text-gray-500">
            Step <span className="font-semibold text-[#1e3a5f]">{step}</span> of {TOTAL_STEPS} —{' '}
            <span className="text-[#c9a84c] font-medium">{STEP_NAMES[step - 1]}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-2xl mx-auto">
          {submitError && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
              {submitError}
            </div>
          )}

          {step === 1 && (
            <QuoteStepCoverage
              value={form.coverage_type}
              zip={form.estimate_zip}
              onChange={(updates) => { updateForm(updates); goNext(); }}
            />
          )}
          {step === 2 && (
            <QuoteStepEstimate
              form={form}
              onChange={updateForm}
              onNext={goNext}
              onBack={goBack}
            />
          )}
          {step === 3 && (
            <QuoteStepApplicant
              form={form}
              isCommercial={isCommercial}
              onChange={updateForm}
              onNext={goNext}
              onBack={goBack}
            />
          )}
          {step === 4 && (
            <QuoteStepCoverageDetails
              form={form}
              onChange={updateForm}
              onNext={goNext}
              onBack={goBack}
            />
          )}
          {step === 5 && (
            <QuoteStepLossHistory
              form={form}
              onChange={updateForm}
              onNext={goNext}
              onBack={goBack}
            />
          )}
          {step === 6 && (
            <QuoteStepPriorInsurance
              form={form}
              onChange={updateForm}
              onNext={goNext}
              onBack={goBack}
            />
          )}
          {step === 7 && (
            <QuoteStepDocuments
              files={uploadedFiles}
              onChange={setUploadedFiles}
              onNext={goNext}
              onBack={goBack}
            />
          )}
          {step === 8 && (
            <QuoteStepReview
              form={form}
              files={uploadedFiles}
              isCommercial={isCommercial}
              submitting={submitting}
              onBack={goBack}
              onSubmit={handleSubmit}
              onEditStep={setStep}
            />
          )}
        </div>
      </div>
    </div>
  );
}
